import type {
  BrandConcessionContentDM,
  ProductSetContentDM,
} from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/MultiSectionView.types';
import { useClassStore } from '@Utils/useClassStore';
import { inject, injectable, postConstruct } from 'inversify';

import { FacebookPixelService } from '~gateways/analytics/facebook-pixel-service';
import { FirebaseService } from '~gateways/analytics/firebase-service';
import container from '~ioc/ioc';
import {
  mapBeginCheckoutDataToFacebookPixel,
  mapBeginCheckoutDataToFirebase,
  mapPurchaseDataToFacebookPixel,
  mapPurchaseDataToFirebase,
  mapViewProductDetailsDataToFacebookPixel,
} from '~types/analytics';
import type { AnalyticsEventData } from '~types/analytics';

@injectable()
export class AnalyticsStore {
  @inject(FirebaseService) private _firebase!: FirebaseService;
  @inject(FacebookPixelService) private _facebookPixel!: FacebookPixelService;

  @postConstruct() postConstruct() {
    this._facebookPixel.initialize();
  }

  logViewBrandConcession = (
    data: AnalyticsEventData<'view_brand_concession'>
  ) => {
    this._firebase.logEvent({ event: 'view_brand_concession', data }).then();
  };

  logViewBrandSection = (data: AnalyticsEventData<'view_brand_section'>) => {
    this._firebase.logEvent({ event: 'view_brand_section', data }).then();
    // TODO implement logging to Ownable Analytics once API endpoint is ready
  };

  logViewProductSet = (data: AnalyticsEventData<'view_product_set'>) => {
    this._firebase.logEvent({ event: 'view_product_set', data }).then();
  };

  logViewProductDetails = (
    data: AnalyticsEventData<'view_product_details'>
  ) => {
    this._firebase.logEvent({ event: 'view_product_details', data }).then();
    this._facebookPixel.trackViewProductDetails(
      mapViewProductDetailsDataToFacebookPixel(data)
    );
  };

  logViewDepartment = (data: AnalyticsEventData<'view_department'>) => {
    this._firebase.logEvent({ event: 'view_department', data }).then();
    // TODO implement logging to Ownable API when ready
  };

  logBrowseFloor = (data: AnalyticsEventData<'browse_floor'>) => {
    this._firebase.logEvent({ event: 'browse_floor', data }).then();
    // TODO implement logging to Ownable API when ready
  };

  logViewFloor = (data: AnalyticsEventData<'view_floor'>) => {
    this._firebase.logEvent({ event: 'view_floor', data }).then();
    // TODO implement logging to Ownable API when ready
  };

  // noinspection JSUnusedGlobalSymbols
  logViewPersonal = (data: AnalyticsEventData<'view_personal'>) => {
    this._firebase.logEvent({ event: 'view_personal', data }).then();
  };

  logProductActionBuy = (data: AnalyticsEventData<'product_action_buy'>) => {
    this._firebase.logEvent({ event: 'product_action_buy', data }).then();
  };

  logProductActionVisit = (data: AnalyticsEventData<'referral'>) => {
    this._firebase.logEvent({ event: 'referral', data }).then();
    // TODO implement logging to Ownable API when ready
  };

  logSignupSubmit = ({ method, userId }: AnalyticsEventData<'sign_up'>) => {
    this._firebase.signUp({ method, user: userId?.toString() ?? '' }).then();
    this._facebookPixel.trackCompleteRegistration(method);
  };

  logBeginCheckout = (data: AnalyticsEventData<'begin_checkout'>) => {
    this._firebase.beginCheckout(mapBeginCheckoutDataToFirebase(data)).then();
    this._facebookPixel.trackPurchase(
      mapBeginCheckoutDataToFacebookPixel(data)
    );
  };

  logPurchase = (data: AnalyticsEventData<'purchase'>) => {
    this._firebase.purchase(mapPurchaseDataToFirebase(data)).then();
    this._facebookPixel.trackPurchase(mapPurchaseDataToFacebookPixel(data));
  };

  logAbandonCheckout = (data: AnalyticsEventData<'abandon_checkout'>) => {
    this._firebase
      .abandonCheckout({
        user: data.userId?.toString() ?? '',
        context: data.step,
        method: data.reason,
      })
      .then();
  };

  logLogin = (data: AnalyticsEventData<'login'>) => {
    this._firebase
      .login({
        user: data.userId,
        method: data.context,
      })
      .then();
  };

  logAppStart = (data: AnalyticsEventData<'app_start'>) => {
    this._firebase
      .logEvent({
        event: 'app_start',
        data,
      })
      .then();
  };
}

export const useAnalyticsStore = () =>
  useClassStore<AnalyticsStore>(container.get(AnalyticsStore));

export function extractViewBrandConcessionAnalyticsData(
  data: BrandConcessionContentDM
): Omit<AnalyticsEventData<'view_brand_concession'>, 'productId' | 'userId'> {
  return {
    // @ts-expect-error
    brandId: data.brandId,
    // @ts-expect-error
    brandName: data.brandName,
    // @ts-expect-error
    categoryId: data.categoryId,
    // @ts-expect-error
    categoryName: data.categoryName,
  };
}

export function extractViewProductSetAnalyticsData(
  data: ProductSetContentDM
): Omit<AnalyticsEventData<'view_product_set'>, 'productId' | 'userId'> {
  return {
    productSetId: data.productSetId,
    productSetName: data.departmentName,
  };
}

export function extractViewBrandSectionAnalyticsData(
  data: BrandConcessionContentDM,
  sectionId: number
): Omit<AnalyticsEventData<'view_brand_section'>, 'userId'> {
  // @ts-expect-error
  const section = [data.header, ...data.sections].find(
    (item) => item.id === sectionId
  );

  return {
    // @ts-expect-error
    brandId: data.brandId,
    // @ts-expect-error
    brandName: data.brandName,
    // @ts-expect-error
    categoryId: data.categoryId,
    // @ts-expect-error
    categoryName: data.categoryName,
    sectionId: section?.id ?? 0,
    sectionName: section?.title ?? '',
  };
}
